import { getTelecomToken } from "../../shared/api/telecom";
import { TelecomTokenStore } from "./telecom-token-store";

/**
 * Service for managing telecom API authentication token on the client.
 * Handles token loading, fetching from backend, and storage.
 */
export class TelecomTokenService {
  constructor(private readonly tokenStore: TelecomTokenStore) {}

  /**
   * Load token from storage.
   */
  async loadToken(): Promise<string | null> {
    return this.tokenStore.load();
  }

  /**
   * Get current token from memory (without loading from storage).
   */
  getToken(): string | null {
    const token = this.tokenStore.getToken();
    // Ensure token is always a string, not an object
    if (token === null) {
      return null;
    }
    if (typeof token === 'string') {
      return token;
    }
    // If token is an object, try to extract string value
    if (typeof token === 'object' && token !== null && 'token' in token) {
      const tokenValue = (token as { token?: unknown }).token;
      return typeof tokenValue === 'string' ? tokenValue : null;
    }
    // Fallback: convert to string
    return String(token);
  }

  /**
   * Fetch token from backend and store it.
   */
  async fetchAndStoreToken(): Promise<string> {
    const response = await getTelecomToken();
    await this.tokenStore.setToken(response.token);
    return response.token;
  }

  /**
   * Ensure token is available.
   * If token is not in memory/storage, fetches it from backend.
   */
  async ensureToken(): Promise<string | null> {
    const currentToken = await this.loadToken();
    
    if (currentToken) {
      // Ensure token is always a string, not an object
      if (typeof currentToken === 'string') {
        return currentToken;
      }
      // If token is an object, try to extract string value
      if (typeof currentToken === 'object' && currentToken !== null && 'token' in currentToken) {
        const tokenValue = (currentToken as { token?: unknown }).token;
        return typeof tokenValue === 'string' ? tokenValue : null;
      }
      // Fallback: convert to string
      return String(currentToken);
    }

    try {
      const token = await this.fetchAndStoreToken();
      // Ensure returned token is a string
      return typeof token === 'string' ? token : String(token);
    } catch (error) {
      console.error("Failed to fetch telecom token:", error);
      return null;
    }
  }

  /**
   * Clear token from memory and storage.
   */
  async clearToken(): Promise<void> {
    await this.tokenStore.clear();
  }

  /**
   * Subscribe to token changes.
   * Returns unsubscribe function.
   */
  subscribe(listener: (token: string | null) => void): () => void {
    return this.tokenStore.subscribe(listener);
  }
}

